import { useState, useEffect } from "react";
import { apiFetch } from "../services/api.js";
import { LocationEditModal } from "../molecules/LocationEditModal.jsx";

function maskPhone(phone) {
  if (!phone || phone.length < 8) return phone;
  const prefix = phone.slice(0, 4);
  const suffix = phone.slice(-4);
  return `${prefix} ${"•".repeat(Math.max(0, phone.length - 8))} ${suffix}`;
}

function StatCard({ value, label, icon }) {
  return (
    <div style={{flex:1,minWidth:100,padding:"16px 14px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:12,textAlign:"center"}}>
      <div style={{fontSize:20,marginBottom:4}}>{icon}</div>
      <div style={{color:"#f1f5f9",fontSize:22,fontWeight:800,fontFamily:"Georgia,serif"}}>{value}</div>
      <div style={{color:"#64748b",fontSize:11,fontWeight:600,marginTop:2}}>{label}</div>
    </div>
  );
}

export function VoterProfile({ voter, voterXp, onBack, onVoterUpdate, onLogout, onSetView }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLocationEdit, setShowLocationEdit] = useState(false);
  const [editingGender, setEditingGender] = useState(false);
  const [genderValue, setGenderValue] = useState(voter?.gender ?? "");
  const [savingGender, setSavingGender] = useState(false);
  // EMAIL-8: Phone editing for email-only voters
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState(voter?.phone ?? "");
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneErr, setPhoneErr] = useState("");

  useEffect(() => {
    const voterKey = voter?.phone || voter?.email;
    if (!voterKey) { setLoading(false); return; }
    // Use /me/profile (JWT) if available, fall back to phone-based lookup
    const endpoint = voter?.phone
      ? `/api/voters/${encodeURIComponent(voter.phone)}/profile`
      : `/api/voters/me/profile`;
    apiFetch(endpoint)
      .then(data => setProfile(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [voter?.phone, voter?.email]);

  const stats = profile?.stats ?? { totalConversations: 0, totalMessagesSent: 0, candidatesInteracted: 0 };
  const xp = profile?.xpBalance ?? voterXp ?? 0;
  const memberSince = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "—";

  return (
    <div style={{maxWidth:480,margin:"0 auto",padding:"32px 18px 80px"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:28}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:20,padding:"2px 5px",lineHeight:1}}>←</button>
        <h1 style={{color:"#f1f5f9",fontSize:22,fontWeight:900,fontFamily:"Georgia,serif",margin:0}}>Your Profile</h1>
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:"40px 0",color:"#64748b"}}>Loading...</div>
      ) : (
        <>
          {/* Email (if available) */}
          {voter?.email && (
            <div style={{marginBottom:20}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase",marginBottom:6}}>Email</div>
              <div style={{padding:"12px 16px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:10,color:"#94a3b8",fontSize:15}}>
                {voter.email}
              </div>
            </div>
          )}

          {/* Phone — editable for email-only voters */}
          <div style={{marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase"}}>Phone</div>
              {!voter?.phone && !editingPhone && (
                <button onClick={() => { setPhoneValue(""); setEditingPhone(true); setPhoneErr(""); }}
                  style={{padding:"3px 10px",background:"#1e3a5f",border:"1px solid #3b82f640",borderRadius:6,color:"#60a5fa",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
                  + Add
                </button>
              )}
            </div>
            {editingPhone ? (
              <div>
                <div style={{display:"flex",gap:8}}>
                  <input
                    type="tel"
                    inputMode="tel"
                    placeholder="+234 801 234 5678"
                    value={phoneValue}
                    onChange={e => { setPhoneValue(e.target.value); setPhoneErr(""); }}
                    style={{flex:1,padding:"10px 14px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:10,color:"#e2e8f0",fontSize:15,fontFamily:"monospace",outline:"none"}}
                  />
                  <button disabled={savingPhone} onClick={async () => {
                    const clean = phoneValue.replace(/[^\d+]/g, "");
                    if (clean.length < 7) { setPhoneErr("Enter a valid phone number"); return; }
                    setSavingPhone(true);
                    try {
                      const res = await apiFetch("/api/voters/me/phone", {
                        method: "PATCH",
                        body: JSON.stringify({ phone: clean }),
                      });
                      const updated = { ...voter, phone: res.phone ?? clean };
                      onVoterUpdate(updated);
                      setEditingPhone(false);
                    } catch (e) {
                      setPhoneErr(e.message || "Failed to save");
                    } finally { setSavingPhone(false); }
                  }} style={{padding:"8px 16px",background:"linear-gradient(135deg,#3b82f6,#2563eb)",border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:700,cursor:savingPhone?"not-allowed":"pointer",fontFamily:"inherit",opacity:savingPhone?.6:1}}>
                    {savingPhone ? "..." : "Save"}
                  </button>
                  <button onClick={() => { setEditingPhone(false); setPhoneErr(""); }} style={{padding:"8px 12px",background:"#090e1a",border:"1px solid #1e2d45",borderRadius:8,color:"#64748b",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>x</button>
                </div>
                {phoneErr && <div style={{marginTop:6,color:"#ef4444",fontSize:12}}>{phoneErr}</div>}
              </div>
            ) : (
              <div style={{padding:"12px 16px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:10,color:"#94a3b8",fontSize:15,fontFamily:"monospace"}}>
                {voter?.phone ? maskPhone(voter.phone) : "Not set"}
              </div>
            )}
            {!voter?.phone && !editingPhone && (
              <div style={{marginTop:6,padding:"8px 12px",background:"#f59e0b10",border:"1px solid #f59e0b25",borderRadius:8,color:"#f59e0b",fontSize:11,lineHeight:1.5}}>
                Required for airtime rewards. Add your phone number to redeem airtime.
              </div>
            )}
          </div>

          {/* Location */}
          <div style={{marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase"}}>Location</div>
              <button onClick={() => setShowLocationEdit(true)}
                style={{padding:"3px 10px",background:"#1e3a5f",border:"1px solid #3b82f640",borderRadius:6,color:"#60a5fa",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
                ✎ Edit
              </button>
            </div>
            <div style={{padding:"12px 16px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:10,color:"#e2e8f0",fontSize:14,display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:18}}>{voter?.flag || "🌍"}</span>
              <span>{[voter?.country, voter?.state, voter?.lga].filter(Boolean).join(" › ") || "Not set"}</span>
            </div>
          </div>

          {/* Gender — VOTER-5 */}
          <div style={{marginBottom:20}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase"}}>Gender</div>
              {!editingGender && (
                <button onClick={() => { setGenderValue(voter?.gender ?? ""); setEditingGender(true); }}
                  style={{padding:"3px 10px",background:"#1e3a5f",border:"1px solid #3b82f640",borderRadius:6,color:"#60a5fa",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
                  ✎ Edit
                </button>
              )}
            </div>
            {editingGender ? (
              <div style={{display:"flex",gap:8}}>
                <div style={{flex:1,position:"relative"}}>
                  <select className="qm-sel" value={genderValue} onChange={e => setGenderValue(e.target.value)}>
                    <option value="">Prefer not to say</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  <span style={{position:"absolute",right:13,top:"50%",transform:"translateY(-50%)",color:"#475569",pointerEvents:"none",fontSize:11}}>▾</span>
                </div>
                <button disabled={savingGender} onClick={async () => {
                  setSavingGender(true);
                  try {
                    // Hotfix v1.27.1: hit the JWT-based endpoint. The prior
                    // call used /api/voters/${voter.phone} which (a) 404'd
                    // for email-only voters (voter.phone was undefined →
                    // URL /api/voters/undefined) and (b) silently dropped
                    // the `gender` field server-side because the legacy
                    // handler whitelist only kept state + lga. The new
                    // /me/gender endpoint resolves by JWT auth_id and
                    // actually writes the column.
                    const res = await apiFetch(`/api/voters/me/gender`, {
                      method: "PATCH",
                      body: JSON.stringify({ gender: genderValue || "prefer_not_to_say" }),
                    });
                    const updated = { ...voter, gender: res?.gender ?? (genderValue || undefined) };
                    onVoterUpdate(updated);
                    setEditingGender(false);
                  } catch {} finally { setSavingGender(false); }
                }} style={{padding:"8px 16px",background:"linear-gradient(135deg,#3b82f6,#2563eb)",border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:700,cursor:savingGender?"not-allowed":"pointer",fontFamily:"inherit",opacity:savingGender?.6:1}}>
                  {savingGender ? "…" : "Save"}
                </button>
                <button onClick={() => setEditingGender(false)} style={{padding:"8px 12px",background:"#090e1a",border:"1px solid #1e2d45",borderRadius:8,color:"#64748b",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
              </div>
            ) : (
              <div style={{padding:"12px 16px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:10,color:"#e2e8f0",fontSize:14}}>
                {voter?.gender === "male" ? "Male" : voter?.gender === "female" ? "Female" : voter?.gender === "other" ? "Other" : "Not set"}
              </div>
            )}
            {!voter?.gender && !editingGender && (
              <div style={{marginTop:6,padding:"8px 12px",background:"#f59e0b10",border:"1px solid #f59e0b25",borderRadius:8,color:"#f59e0b",fontSize:11,lineHeight:1.5}}>
                💡 Setting your gender helps candidates tailor responses to your perspective.
              </div>
            )}
          </div>

          {/* Member since */}
          <div style={{marginBottom:28}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase",marginBottom:6}}>Member Since</div>
            <div style={{padding:"12px 16px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:10,color:"#94a3b8",fontSize:14}}>
              {memberSince}
            </div>
          </div>

          {/* XP Balance */}
          <div style={{marginBottom:28}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase"}}>XP Balance</div>
              <button onClick={() => onSetView("shop")}
                style={{padding:"3px 10px",background:"#f59e0b12",border:"1px solid #f59e0b30",borderRadius:6,color:"#f59e0b",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
                🛍 Shop
              </button>
            </div>
            <div style={{padding:"14px 16px",background:"linear-gradient(135deg,#f59e0b12,#f59e0b06)",border:"1px solid #f59e0b30",borderRadius:10,display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:22}}></span>
              <span style={{color:"#f59e0b",fontSize:28,fontWeight:900,fontFamily:"Georgia,serif"}}>{xp}</span>
              <span style={{color:"#94a3b8",fontSize:13}}>XP</span>
            </div>
          </div>

          {/* Activity Stats */}
          <div style={{marginBottom:32}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase",marginBottom:10}}>Activity</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <StatCard value={stats.candidatesInteracted} label="Candidates" icon="🧑‍💼" />
              <StatCard value={stats.totalConversations} label="Conversations" icon="💬" />
              <StatCard value={stats.totalMessagesSent} label="Questions" icon="❓" />
            </div>
          </div>

          {/* Logout */}
          <button onClick={onLogout}
            style={{width:"100%",padding:"14px",background:"#ef444412",border:"1px solid #ef444430",borderRadius:11,color:"#ef4444",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            Log Out
          </button>
        </>
      )}

      {/* Location edit modal */}
      {showLocationEdit && voter && (
        <LocationEditModal
          voter={voter}
          onClose={() => setShowLocationEdit(false)}
          onUpdate={(updated) => { onVoterUpdate(updated); setProfile(prev => prev ? { ...prev, state: updated.state, lga: updated.lga } : prev); }}
        />
      )}
    </div>
  );
}

export default VoterProfile;
